import {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { faBowlFood, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PaperClipIcon } from "@heroicons/react/20/solid";
import { t } from "i18next";

import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";
import { ButtonSoft } from "@/shared/components/atoms/ButtonSoft";
import { InputTextRight } from "@/shared/components/atoms/InputTextRight";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { TextArea } from "@/shared/components/atoms/TextArea";
import ListSuplementation from "@/shared/components/organisms/ListSuplementation";
import { ModalAcceptSearchTraining } from "@/shared/components/organisms/ModalAcceptSearchTraining";
import { ModalAddNotes } from "@/shared/components/organisms/ModalAddNotes";
import { Select } from "@/shared/components/organisms/Select";
import { NutritionSideBar } from "@/shared/components/organisms/SideBarNutrition";
import { TabsUser } from "@/shared/components/organisms/TabsUser";
import { ToggleText } from "@/shared/components/organisms/ToggleText";
import { useUserQuery } from "@/app/users/users.api";
import { useQuery } from "@/shared/hooks/query";
import { usePersonalInformationQuery } from "@/app/personal-informations/personal-informations.api";
import { useFormik } from "formik";
import {
  nutritionObjectiveOptions,
  NutritionObjectives,
} from "@/app/nutrition-wizard/nutrition-objectives";
import { Meal } from "@/app/nutrition/meal.enum";
import {
  indexToOption,
  Option,
  optionToIndex,
} from "@/app/nutrition/option.enum";

import {
  activeOptions,
  EditorType,
  searchableOptions,
  skipOptions,
} from "@/app/nutrition/nutrition-editor";

import {
  useDishesQuery,
  useLazyDishQuery,
  useLazyEquivalencesQuery,
  useLazyNutritionalInformationsQuery,
  useNutritionEditableQuery,
  useNutritionQuery,
  useProductNutritionsQuery,
  useSuplementationsQuery,
  useUpdateNutritionMutation,
} from "@/app/nutrition/nutrition.api";
import { NutritionMealTable } from "@/shared/components/organisms/NutritionMealTable";
import { useDebounce } from "@/shared/hooks/debounce";
import { NutritionMacroTable } from "@/shared/components/organisms/NutritionMacroTable";
import { EditorMode } from "@/app/nutrition/editor-mode.enum";
import { Macros, MACROS_INITIAL_VALUE } from "@/app/nutrition/macros";
import { nutritionSearchInitialValues } from "@/app/nutrition/search.schema";
import { useNutritionProgressQuery } from "@/app/progress-nutritions/progress-nutritions.api";
import { AcceptOrCancelModal } from "@/shared/components/organisms/AcceptOrCancelModal";

import { toasts } from "@/shared/utils/toasts";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";

export const EditNutritionPages: FC<any> = () => {
  const navigate = useNavigate();
  const query = useQuery();

  const [editorMode, setEditorMode] = useState<EditorMode>(
    EditorMode.TrainingDay
  );
  const [searchableModalVisible, setSearchableModalVisible] = useState(false);
  const [fromExpiredToActiveModalVisible, setFromExpiredToActiveModalVisible] =
    useState(false);
  const [fromPlannedToActiveModalVisible, setFromPlannedToActiveModalVisible] =
    useState(false);
  const [toActiveModalVisible, setToActiveModalVisible] = useState(false);

  const [currentMacroTab, setCurrentMacroTab] = useState(0);
  const [currentOption, setCurrentOption] = useState({
    [Meal.Breakfast]: Option.First,
    [Meal.Lunch]: Option.First,
    [Meal.Meal]: Option.First,
    [Meal.Snack]: Option.First,
    [Meal.Dinner]: Option.First,
    [Meal.Redinner]: Option.First,
  });
  const [equivalencesCache, setEquivalencesCache] = useState<
    Record<string, any>
  >({});
  const [nutritionalInformationsCache, setNutritionalInformationsCache] =
    useState<Record<string, any>>({});

  const editorType = useMemo(() => {
    return query.get("type");
  }, [query]);

  const id = useMemo(() => {
    return query.get("id");
  }, [query]);

  const userId = useMemo(() => {
    return query.get("user_id");
  }, [query]);

  const userQuery = useUserQuery(
    {
      id: userId,
    },
    { skip: !userId }
  );

  const personalInformationQuery = usePersonalInformationQuery({
    id: 0,
    userId,
    status: 1,
  });

  const nutritionEditableQuery = useNutritionEditableQuery(
    {
      id,
      userId,
    },
    { skip: !id }
  );
  const activeProgressNutritionQuery = useNutritionProgressQuery(
    {
      status: 1,
      userId: userId,
    },
    { skip: !id }
  );
  const draftProgressNutritionQuery = useNutritionProgressQuery(
    {
      status: 2,
      userId: userId,
    },
    { skip: !id }
  );
  const plannedProgressNutritionQuery = useNutritionProgressQuery(
    {
      status: 3,
      userId: userId,
    },
    { skip: !id }
  );
  const completedProgressNutritionQuery = useNutritionProgressQuery(
    {
      status: 0,
      userId: userId,
    },
    { skip: !id }
  );

  const editorProgressNutrition = useMemo(() => {
    if (
      completedProgressNutritionQuery.isSuccess &&
      activeProgressNutritionQuery.isSuccess &&
      draftProgressNutritionQuery.isSuccess &&
      plannedProgressNutritionQuery.isSuccess &&
      nutritionEditableQuery.isSuccess
    ) {
      const plan = nutritionEditableQuery.data?.plan;

      if (!plan) return null;

      if (draftProgressNutritionQuery.data) {
        return draftProgressNutritionQuery.data;
      }

      if (completedProgressNutritionQuery.data?.position === plan?.position) {
        return completedProgressNutritionQuery.data;
      }

      if (activeProgressNutritionQuery.data?.position === plan?.position) {
        return activeProgressNutritionQuery.data;
      }

      if (plannedProgressNutritionQuery.data?.position === plan?.position) {
        return plannedProgressNutritionQuery.data;
      }
    }
    return null;
  }, [
    nutritionEditableQuery,
    completedProgressNutritionQuery,
    activeProgressNutritionQuery,
    draftProgressNutritionQuery,
    plannedProgressNutritionQuery,
  ]);

  const productsQuery = useProductNutritionsQuery({
    tableNutrition: "estetica_nutrition",
  });

  const [updateNutrition, updateNutritionResult] = useUpdateNutritionMutation();
  const [nutritionalInformationsQuery] = useLazyNutritionalInformationsQuery();
  const [equivalencesQuery] = useLazyEquivalencesQuery();
  const [dishQuery] = useLazyDishQuery();

  const formik = useFormik({
    initialValues: {
      id: id,
      userId: userId,
      objective: undefined,
      name: "",
      duration: 1,
      note: "",
      editorType: editorType,
      searchable: 0,
      toActive: 0,
      fromPlannedToActive: 0,
      fromExpiredToActive: 0,
      position: undefined,
      nutritionTable: nutritionEditableQuery?.data?.plan?.tableNutrition,
      nutritionId: nutritionEditableQuery?.data?.plan?.nutritionId,
      macros: Object.entries(Meal).reduce(
        (nutrition, [, meal]) => ({
          ...nutrition,
          [meal]: Object.entries(Option).reduce(
            (options, [, option]) => ({
              ...options,
              [option]: {
                [EditorMode.RestDay]: [],
                [EditorMode.TrainingDay]: [],
              },
            }),
            []
          ),
        }),
        {}
      ),
      suplementations: Object.entries(Meal).reduce(
        (suplementations, [, meal]) => ({
          ...suplementations,
          [meal]: [],
        }),
        {}
      ),
      nutrition: Object.entries(Meal).reduce(
        (nutrition, [, meal]) => ({
          ...nutrition,
          [meal]: Object.entries(Option).reduce(
            (options, [, option]) => ({
              ...options,
              [option]: undefined,
            }),
            []
          ),
        }),
        {}
      ),
      swaps: Object.entries(Meal).reduce(
        (swaps, [, meal]) => ({
          ...swaps,
          [meal]: [],
        }),
        {}
      ),
      pointers: Object.entries(Meal).reduce(
        (swaps, [, meal]) => ({
          ...swaps,
          [meal]: 0,
        }),
        {}
      ),
      counters: Object.entries(Meal).reduce(
        (swaps, [, meal]) => ({
          ...swaps,
          [meal]: 0,
        }),
        {}
      ),
    },
    onSubmit: (values) => {
      setSearchableModalVisible(true);
    },
  });

  useEffect(() => {
    if (updateNutritionResult.isSuccess) {
      toasts.success(
        "Operacion exitosa",
        "Plan de nutrición guardado con éxito"
      );

      navigate(`/users/${userId}`);
    }

    if (updateNutritionResult.isError) {
      toasts.danger(
        "Operacion no exitosa",
        "Plan de nutrición no guardado con éxito"
      );
    }
  }, [updateNutritionResult, userId]);

  useEffect(() => {
    if (editorProgressNutrition && !formik.values.position) {
      formik.setFieldValue("position", editorProgressNutrition.position);
    }
  }, [editorProgressNutrition, formik]);

  useEffect(() => {
    if (nutritionEditableQuery.data?.plan) {
      formik.setFieldValue(
        "duration",
        nutritionEditableQuery.data?.plan?.duration
      );
    }
  }, [nutritionEditableQuery.data?.plan?.duration]);

  useEffect(() => {
    if (nutritionEditableQuery.isSuccess) {
      setNutritionalInformationsCache((nutritionalInformation) => ({
        ...nutritionalInformation,
        ...nutritionEditableQuery.data?.nutritionalInformations,
      }));

      setEquivalencesCache((equivalences) => ({
        ...equivalences,
        ...nutritionEditableQuery.data?.equivalences,
      }));

      console.debug(nutritionEditableQuery.data?.plan?.plan);
      formik.setFieldValue("name", nutritionEditableQuery.data?.plan?.plan);
      formik.setFieldValue(
        "objective",
        nutritionEditableQuery.data?.plan?.objective
      );
      formik.setFieldValue("duration", nutritionEditableQuery.data?.plan?.week);
      formik.setFieldValue("nutrition", nutritionEditableQuery.data?.nutrition);
      formik.setFieldValue("swaps", nutritionEditableQuery.data?.swaps);
      formik.setFieldValue("counters", nutritionEditableQuery.data?.counts);
      formik.setFieldValue("macros", nutritionEditableQuery.data?.macros);
      formik.setFieldValue(
        "suplementations",
        nutritionEditableQuery.data?.supplementations
      );
    }
  }, [nutritionEditableQuery]);

  const { fibers, fats, carbohydrates, proteins, calories } =
    useMemo<any>(() => {
      return Object.values(formik.values.macros).reduce((_, options) => {
        const products = Object.values(
          options[indexToOption(currentMacroTab)][editorMode]
        );

        const currentOption = products.reduce<Macros>((_: any, macros: any) => {
          return {
            fibers: _.fibers + macros.fibers,
            fats: _.fats + macros.fats,
            calories: _.calories + macros.calories,
            carbohydrates: _.carbohydrates + macros.carbohydrates,
            proteins: _.proteins + macros.proteins,
          };
        }, MACROS_INITIAL_VALUE);

        return {
          fibers: _.fibers + (currentOption?.fibers ?? 0),
          calories: _.calories + (currentOption?.calories ?? 0),
          carbohydrates: _.carbohydrates + (currentOption?.fibers ?? 0),
          fats: _.fats + (currentOption?.fats ?? 0),
          proteins: _.proteins + (currentOption?.proteins ?? 0),
        };
      }, MACROS_INITIAL_VALUE);
    }, [formik.values.macros, currentMacroTab, editorMode]);

  const handleSubmit = useCallback(() => {
    updateNutrition(formik.values);
  }, [formik.values]);

  const handleSetDish = useCallback(
    async (meal, dish) => {
      const products = await dishQuery(dish).unwrap();

      dish.products.map((product) => {
        equivalencesQuery({ product })
          .unwrap()
          .then((response) => {
            setEquivalencesCache((equivalencesCache) => ({
              ...equivalencesCache,
              [product]: response,
            }));
          });
      });

      dish.products.map((product) => {
        nutritionalInformationsQuery({ product })
          .unwrap()
          .then((response) => {
            setNutritionalInformationsCache((nutritionalInformationsCache) => ({
              ...nutritionalInformationsCache,
              [product]: response,
            }));
          });
      });

      formik.setFieldValue(`nutrition.${meal}.[${currentOption[meal]}]`, {
        gallery: {
          id: dish.id,
          image: dish.image,
        },
        products: products.map((product) => ({
          product: product.product,
          portion: product.portion,
          portionAlt: product.portionAlt,
          portionRest: product.portionRest,
          portionRestAlt: product.portionRestAlt,
        })),
      });
    },
    [formik]
  );

  const handleSetProduct = useCallback(
    async (product: any, meal: Meal, option: Option, index: number) => {
      nutritionalInformationsQuery({ product: product.name })
        .unwrap()
        .then((response) => {
          setNutritionalInformationsCache((nutritionalInformationsCache) => ({
            ...nutritionalInformationsCache,
            [product.name]: response,
          }));
        });

      equivalencesQuery({ product: product.name })
        .unwrap()
        .then((response) => {
          setEquivalencesCache((equivalencesCache) => ({
            ...equivalencesCache,
            [product.name]: response,
          }));
        });

      formik.setFieldValue(
        `nutrition.[${meal}].[${option}].products.[${index}]`,
        {
          product: product.name,
          portion: "0 gr",
          portionAlt: undefined,
          portionRest: "0 gr",
          portionRestAlt: undefined,
        }
      );
    },
    []
  );

  const handleSetSupplementation = useCallback(
    (suplementation: any, meal: Meal) => {
      formik.setFieldValue(`suplementations.[${meal}]`, [
        ...formik.values.suplementations[meal],
        suplementation,
      ]);
    },
    []
  );

  const handleCurrentOptionChange = useCallback(
    (option: Option, meal: Meal) => {
      setCurrentOption((currentOption) => ({
        ...currentOption,
        [meal]: option,
      }));
    },
    []
  );

  const searchFormik = useFormik({
    initialValues: nutritionSearchInitialValues,
    onSubmit: () => {},
  });

  useEffect(() => {
    searchFormik.setFieldValue("objective", formik.values.objective);
  }, [formik.values.objective]);

  const debouncedSearch = useDebounce(searchFormik.values, 300);

  const dishesQuery = useDishesQuery({
    search: debouncedSearch.dishes,
    page: debouncedSearch.page,
    meal: debouncedSearch.meal,
    objective: debouncedSearch.objective,
  });

  const suplementationsQuery = useSuplementationsQuery({
    search: debouncedSearch.suplementation,
  });

  const handleOptionDelete = useCallback(
    (meal: Meal, option: Option) => {
      const isEmpty =
        formik.values.nutrition[meal][option].products?.length === 0;

      if (isEmpty) {
        toasts.warning(
          t("contexts.shared.invalid_action"),
          t("contexts.nutrition.editor_option_empty_error")
        );
        return;
      }

      const nextOptionIndex = optionToIndex(option) + 1;
      const nextOption =
        formik.values.nutrition[meal][indexToOption(nextOptionIndex)];
      const hasNextOption = nextOption.products?.length > 0;

      if (hasNextOption) {
        toasts.warning(
          t("contexts.shared.invalid_action"),
          t("contexts.nutrition.editor_option_delete_error")
        );
      } else {
        formik.setFieldValue(`nutrition.[${meal}].[${option}]`, undefined);
      }
    },
    [formik.values]
  );

  const handleProductDelete = useCallback(
    (meal: Meal, option: Option, index: number) => {
      const products = formik.values.nutrition[meal][option].products;

      formik.setFieldValue(`nutrition.[${meal}].[${option}].products`, [
        ...products.slice(0, index),
        ...products.slice(index + 1),
      ]);
    },
    [formik.values.nutrition]
  );

  const handleOptionSwap = useCallback(
    (meal: Meal, option: Option) => {
      const alternativeOptionIndex = formik.values.pointers[meal];
      const alternativeOption = `${indexToOption(alternativeOptionIndex)}-alt`;
      const alternative = formik.values.swaps[meal][alternativeOption];
      const original = formik.values.nutrition[meal][option];

      formik.setFieldValue(`nutrition.[${meal}].[${option}]`, alternative);
      formik.setFieldValue(`swaps.[${meal}].[${alternativeOption}]`, original);

      if (formik.values.pointers[meal] === formik.values.counters[meal] - 1) {
        formik.setFieldValue(`pointers.[${meal}]`, 0);
      } else {
        formik.setFieldValue(
          `pointers.[${meal}]`,
          formik.values.pointers[meal] + 1
        );
      }
    },
    [
      formik.values.swaps,
      formik.values.nutrition,
      formik.values.pointers,
      formik.values.counters,
    ]
  );

  const nutritionMealTableRefs = {
    [Meal.Breakfast]: useRef<any>(null),
    [Meal.Lunch]: useRef<any>(null),
    [Meal.Meal]: useRef<any>(null),
    [Meal.Snack]: useRef<any>(null),
    [Meal.Dinner]: useRef<any>(null),
    [Meal.Redinner]: useRef<any>(null),
  };

  const handleOptionRight = useCallback(
    (meal: Meal, option: Option) => {
      const isEmpty =
        formik.values.nutrition[meal][option].products?.length === 0;

      if (isEmpty) {
        toasts.warning(
          t("contexts.shared.invalid_action"),
          t("contexts.nutrition.editor_option_empty_error")
        );
        return;
      }

      const nextOptionIndex = optionToIndex(option) + 1;
      const nextOption =
        formik.values.nutrition[meal][indexToOption(nextOptionIndex)];
      const hasNextOption = nextOption.products?.length > 0;

      if (!hasNextOption) {
        toasts.warning(
          t("contexts.shared.invalid_action"),
          t("contexts.nutrition.editor_option_right_error")
        );
        return;
      }

      const rightOptionKey = indexToOption(optionToIndex(option) + 1);

      const currentOption = formik.values.nutrition[meal][option];
      const rightOption = formik.values.nutrition[meal][rightOptionKey];

      formik.setFieldValue(
        `nutrition.[${meal}].[${rightOptionKey}]`,
        currentOption
      );
      formik.setFieldValue(`nutrition.[${meal}].[${option}]`, rightOption);

      nutritionMealTableRefs[meal].current?.setCurrentTab(
        optionToIndex(rightOptionKey)
      );
    },
    [formik.values.nutrition]
  );

  const handleOptionLeft = useCallback(
    (meal: Meal, option: Option) => {
      const isEmpty =
        formik.values.nutrition[meal][option].products?.length === 0;

      if (isEmpty) {
        toasts.warning(
          t("contexts.shared.invalid_action"),
          t("contexts.nutrition.editor_option_empty_error")
        );
        return;
      }

      const isFirst = optionToIndex(option) === 0;

      if (isFirst) {
        toasts.warning(
          t("contexts.shared.invalid_action"),
          t("contexts.nutrition.editor_option_left_error")
        );
        return;
      }

      const leftOptionKey = indexToOption(optionToIndex(option) - 1);

      const currentOption = formik.values.nutrition[meal][option];
      const rightOption = formik.values.nutrition[meal][leftOptionKey];

      formik.setFieldValue(
        `nutrition.[${meal}].[${leftOptionKey}]`,
        currentOption
      );
      formik.setFieldValue(`nutrition.[${meal}].[${option}]`, rightOption);
      nutritionMealTableRefs[meal].current?.setCurrentTab(
        optionToIndex(leftOptionKey)
      );
    },
    [formik.values.nutrition]
  );

  const handleSearchableCancel = useCallback(() => {
    setSearchableModalVisible(false);
  }, []);

  const handleSearchableAccept = useCallback(() => {
    setSearchableModalVisible(false);

    if (editorProgressNutrition?.status === 1) {
      /**
       * Está modificando el plan activo, por lo cual directamente se guarda el plan
       */
      handleSubmit();
    } else if (editorProgressNutrition?.status === 3) {
      /**
       * Si estás editando el plan planeado solamente preguntar si lo quiere activar, si no
       * lo dejas en planeado y después se debe guardar
       */
      setFromPlannedToActiveModalVisible(true);
    } else if (editorProgressNutrition?.finished) {
      /**
       * Si está modificando un plan finalizado preguntarle cuanto durará y asignar ahora siempre
       */
      setFromExpiredToActiveModalVisible(true);
      formik.setFieldValue("fromExpiredToActive", 1);
      formik.setFieldValue("toActive", 1);
    } else if (
      editorProgressNutrition?.status !== 3 &&
      activeProgressNutritionQuery.data?.status === 1
    ) {
      /**
       * Si el status es diferente de 3 y no hay plan activo entonces preguntar si el plan se asignará ahora
       */
      setToActiveModalVisible(true);
    } else {
      handleSubmit();
    }
  }, [editorProgressNutrition, formik]);

  const handleActiveAccept = useCallback(() => {
    handleSubmit();
  }, [formik]);

  const handleActiveCancel = useCallback(() => {
    setToActiveModalVisible(false);
  }, [formik]);

  const handleFromPlannedToActiveAccept = useCallback(() => {
    handleSubmit();
  }, [formik]);

  const handleFromPlannedToActiveCancel = useCallback(() => {
    setFromPlannedToActiveModalVisible(false);
  }, []);

  const handleExpiredAccept = useCallback(() => {
    handleSubmit();
  }, [formik]);

  const handleExpiredCancel = useCallback(() => {
    setFromExpiredToActiveModalVisible(false);
  }, []);

  //DATOS DE BUSQUEDA DIALOG
  const optionsSelect = [
    { id: 1, name: t("assisteds.controller.select_assisted") },
    { id: 2, name: t("nutrition-wizard.views.breakfast") },
    { id: 3, name: t("nutrition-wizard.views.lunch") },
    { id: 4, name: t("nutrition-wizard.views.dinner") },
    { id: 5, name: t("user.views.media_manana") },
    { id: 6, name: t("user.views.merienda") },
    { id: 7, name: t("user.views.posCena") },
  ];
  const [selectOptions, setSelectOptions] = useState(optionsSelect[0]);
  //FIN DATOS DE BUSQUEDA DIALOG

  //OPEN DIV EN MOVILES
  const [openNewDivForNutrition, setNewDivForNutrition] = useState(false);
  const handleOpenNewDivForNutrition = () =>
    setNewDivForNutrition(!openNewDivForNutrition);

  //TOGGLE FOOD
  const [toggleFood, setToggleFood] = useState(false);
  //OPTIONS SUPLEMENTATION
  const optionsSuplementation = [
    { id: 1, name: t("user.views.multivitaminico") },
    { id: 2, name: t("user.views.betaalanina") },
    { id: 3, name: t("user.views.cafeina") },
    { id: 4, name: t("user.views.l-citrulina") },
    { id: 5, name: t("user.views.panax_ginseng") },
    { id: 6, name: t("user.views.eaa_s") },
    { id: 7, name: t("user.views.ashwagandha") },
    { id: 8, name: t("user.views.l-carnitina") },
    { id: 9, name: t("user.views.melatonina") },
    { id: 10, name: t("user.views.omega_3") },
    { id: 11, name: t("user.views.creatina") },
  ];
  //FOOD SUPLEMENTATION
  const TabsFood = [
    { id: 1, name: t("nutrition-wizard.views.meals") },
    { id: 2, name: t("nutrition-wizard.views.suplementation") },
  ];

  const [selectTabFood, setSelectTabFood] = useState(1);
  const handleTabChangeFood = (tabId: any) => {
    setSelectTabFood(tabId);
  };
  //Listado de comidas
  const optionsComidas = [
    { id: 1, products: ["Pechuga de pollo", "Quinoa", "Brócoli", "Aguacate"] },
    { id: 2, products: ["Fresas", "Yogurt griego", "Almendras", "Chía"] },
    {
      id: 3,
      products: ["Salmón", "Espinacas", "Batata", "Ensalada de rúcula"],
    },
    {
      id: 4,
      products: [
        "Tofu",
        "Arroz integral",
        "Verduras al vapor",
        "Salsa de soya baja en sodio",
      ],
    },
    {
      id: 5,
      products: [
        "Ensalada de kale",
        "Garbanzos",
        "Aguacate",
        "Semillas de girasol",
      ],
    },
    {
      id: 6,
      products: [
        "Batido verde",
        "Espinacas",
        "Pepino",
        "Manzana verde",
        "Jengibre",
      ],
    },
    {
      id: 7,
      products: ["Atún", "Tomate", "Lechuga", "Aceitunas", "Pimientos"],
    },
    { id: 8, products: ["Pavo", "Brócoli", "Quinoa", "Nueces"] },
    { id: 9, products: ["Pavo", "Quinoa"] },
  ];

  const [selectTabsKpis, setSelectTabsKpis] = useState(1);
  const tabsKpis = [
    { id: 0, name: "1" },
    { id: 1, name: "2" },
    { id: 2, name: "3" },
    { id: 3, name: "4" },
    { id: 4, name: "5" },
    { id: 5, name: "6" },
    { id: 6, name: "7" },
  ];
  const handleTabKpis = (tabId: any) => {
    setSelectTabsKpis(tabId);
  };

  //ABRIR KPIS DE CALORIAS
  const [openKpisCalories, f] = useState(true);
  const handleOpenKpisCalories = () => f(!openKpisCalories);
  //OPEN PRODUCT TABLE
  const [openProductTable, setOpenProductTable] = useState(false);
  const handleOpenProductTable = () => setOpenProductTable(!openProductTable);
  //SELECT PARA AGREGAR
  const optionsAddProduct = [
    { id: 1, name: "Pollo" },
    { id: 2, name: "Carne" },
    { id: 3, name: "Arroz" },
    { id: 4, name: "Verduras" },
    { id: 5, name: "Avena" },
  ];
  const [selectAddProduct, setSelectAddProduct] = useState(
    optionsAddProduct[0]
  );
  //MODAL DE ACEPTAR
  const [openModalAcceptSearchTraining, setOpenModalAcceptSearchTraining] =
    useState(false);
  const handleOpenModalAcceptSearchTraining = () =>
    setOpenModalAcceptSearchTraining(!openModalAcceptSearchTraining);
  //FIN MODAL DE ACEPTAR

  //SELECT SI O NO GUARDAR PLAN PARA BUSCAR
  const optionsSelectSavePlan = [
    { id: 1, name: t("general.yes") },
    { id: 2, name: t("general.no") },
  ];
  const [selectSavePlan, setSelectSavePlan] = useState(
    optionsSelectSavePlan[0]
  );
  //FIN SELECT SI O NO GUARDAR PLAN PARA BUSCAR

  //OPEN MODAL NOTAS
  const [openModalNotes, setOpenModalNotes] = useState(false);
  const handleOpenModalNotes = () => setOpenModalNotes(!openModalNotes);
  //FIN OPEN MODAL NOTAS

  //OPEN MODAL SUGERIR NEW PLATE
  const [openModalSuggestNewPlate, setOpenModalSuggestNewPlate] =
    useState(false);
  const handleOpenModalSuggestNewPlate = () =>
    setOpenModalSuggestNewPlate(!openModalSuggestNewPlate);

  const calculatedEndDate = useMemo(() => {
    console.debug({
      editorProgressNutrition,
      duration: formik.values.duration,
      startDate: editorProgressNutrition?.startDate,
    });
    if (!editorProgressNutrition?.startDate || !formik.values.duration)
      return undefined;

    const startDate = DateTime.fromISO(editorProgressNutrition?.startDate);

    return startDate
      .plus({
        weeks: formik.values.duration,
      })
      .toFormat("yyyy-MM-dd");
  }, [formik.values.duration]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-12 h-full w-full">
      <div className="md:hidden col-span-12 px-4 h-full">
        <div
          onClick={handleOpenNewDivForNutrition}
          className="block w-full rounded-lg mt-5 py-5 border-2 border-dashed border-gray-300 text-center"
        >
          <FontAwesomeIcon
            icon={faBowlFood}
            className="mx-auto h-24 w-12 text-gray-400"
          />

          <span className="mt-2 block text-sm font-semibold text-gray-900">
            {t("user.views.add_food")}
          </span>
        </div>
      </div>
      {/* Capa Izquierda */}
      <div className=" col-span-12 md:col-span-11 flex flex-col px-4 h-full">
        {/* TITULO */}
        <h1 className="text-base font-semibold leading-6 text-gray-900 border-b border-gray-200 pb-3 mt-10 mb-5">
          {t("chat.app.nutrition_plan")}
        </h1>

        {/* CAPA DE INFORMACIÓN PERSONAL */}
        <div className="flex justify-between border-b border-gray-200 pb-3 mb-5">
          {userQuery.isLoading ? (
            <h1 className="text-gray-700 font-bold text-lg h-6 bg-gray-300 animate-pulse rounded-sm w-32" />
          ) : (
            <h1 className="text-gray-700 font-bold text-lg">
              {userQuery.data?.user?.name}
            </h1>
          )}
          {personalInformationQuery.isLoading ? (
            <h1 className="text-gray-700 font-bold text-lg h-6 bg-gray-300 animate-pulse rounded-sm w-32" />
          ) : (
            <h1 className="text-gray-700 font-bold text-lg">
              {personalInformationQuery.data?.sex} /{" "}
              {`${personalInformationQuery.data?.age} ${t("contexts.shared.years")}`}
            </h1>
          )}
        </div>

        {nutritionEditableQuery.isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col gap-2">
              <div className="h-4 bg-gray-300 animate-pulse w-24 rounded-sm" />
              <div className="h-8 bg-gray-300 animate-pulse w-full rounded-sm" />
            </div>
            <div className="flex flex-col gap-2">
              <div className="h-4 bg-gray-300 animate-pulse w-32 rounded-sm" />
              <div className="h-8 bg-gray-300 animate-pulse w-full rounded-sm" />
            </div>
            <div className="flex flex-col gap-2">
              <div className="h-4 bg-gray-300 animate-pulse w-24 rounded-sm" />
              <div className="h-8 bg-gray-300 animate-pulse w-full rounded-sm" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputWithCornerHint
              label={t("contexts.nutrition.plan_name")}
              type="text"
              name="name"
              onChange={formik.handleChange}
              value={formik.values.name}
            />
            <Select
              name="objective"
              selected={nutritionObjectiveOptions.find(
                (option) => option.key === formik.values.objective
              )}
              setSelected={(option) =>
                formik.setFieldValue("objective", option?.key)
              }
              options={nutritionObjectiveOptions}
              tittle={t("contexts.nutrition.objective")}
            />
            {editorType === EditorType.Active &&
            editorProgressNutrition?.status === 0 ? (
              <InputTextRight
                label={t("contexts.nutrition.plan_duration")}
                spanRight={t("contexts.shared.weeks")}
                type="text"
                value="Estás modificando un plan completado"
                disabled
              />
            ) : (
              <InputTextRight
                label={t("contexts.nutrition.plan_duration")}
                spanRight={t("contexts.shared.weeks")}
                type="text"
                inputLarge={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name="duration"
                value={formik.values.duration}
              />
            )}
          </div>
        )}
        {formik.values.objective !== NutritionObjectives.Health && (
          <Fragment>
            <h1 className="text-base font-semibold leading-6 text-gray-900 border-b border-gray-200 pb-3 mt-10">
              {t("contexts.nutrition.macronutrients")}
            </h1>
            <TabsUser
              tabs={tabsKpis}
              selectedTab={currentMacroTab}
              handleTabChange={(tab) => {
                setCurrentMacroTab(() => tab);
              }}
            />

            <div className="flex flex-col mt-2 gap-5 lg:flex-row lg:gap-5">
              <NutritionMacroTable
                isLoading={
                  nutritionEditableQuery?.isLoading ||
                  nutritionEditableQuery?.isFetching
                }
                fibers={fibers}
                fats={fats}
                carbohydrates={carbohydrates}
                proteins={proteins}
                calories={calories}
              />
              <div className="flex items-start gap-5 flex-row items-center">
                <ToggleText
                  checked={editorMode === EditorMode.TrainingDay}
                  onChange={(enabled) =>
                    setEditorMode(
                      enabled ? EditorMode.TrainingDay : EditorMode.RestDay
                    )
                  }
                  texto={
                    editorMode === EditorMode.TrainingDay
                      ? t("contexts.nutrition.training_day")
                      : t("contexts.nutrition.rest_day")
                  }
                />
                <ButtonSoft
                  size="large"
                  text={t("nutrition-wizard.views.add_none")}
                  onClick={handleOpenModalNotes}
                />
              </div>
            </div>
          </Fragment>
        )}
        <NutritionMealTable
          counter={formik.values.counters[Meal.Breakfast]}
          onDeleteProduct={handleProductDelete}
          objective={formik.values.objective}
          onSetProduct={handleSetProduct}
          onOptionRight={handleOptionRight}
          onOptionLeft={handleOptionLeft}
          onOptionDelete={handleOptionDelete}
          onOptionSwap={handleOptionSwap}
          editorMode={editorMode}
          equivalences={equivalencesCache}
          nutritionalInformations={nutritionalInformationsCache}
          meal={Meal.Breakfast}
          suplementations={formik.values.suplementations[Meal.Breakfast]}
          options={formik.values.nutrition[Meal.Breakfast] ?? {}}
          macros={formik.values.macros[Meal.Breakfast]}
          onCurrentOptionChange={handleCurrentOptionChange}
          onChange={formik.handleChange}
          setFieldValue={formik.setFieldValue}
          products={productsQuery?.data ?? []}
          ref={nutritionMealTableRefs[Meal.Breakfast]}
        />
        <NutritionMealTable
          counter={formik.values.counters[Meal.Lunch]}
          onDeleteProduct={handleProductDelete}
          objective={formik.values.objective}
          onSetProduct={handleSetProduct}
          onOptionRight={handleOptionRight}
          onOptionLeft={handleOptionLeft}
          onOptionDelete={handleOptionDelete}
          onOptionSwap={handleOptionSwap}
          editorMode={editorMode}
          equivalences={equivalencesCache}
          nutritionalInformations={nutritionalInformationsCache}
          meal={Meal.Lunch}
          suplementations={formik.values.suplementations[Meal.Lunch]}
          options={formik.values.nutrition[Meal.Lunch] ?? {}}
          macros={formik.values.macros[Meal.Lunch]}
          onCurrentOptionChange={handleCurrentOptionChange}
          onChange={formik.handleChange}
          setFieldValue={formik.setFieldValue}
          products={productsQuery?.data ?? []}
          ref={nutritionMealTableRefs[Meal.Lunch]}
        />
        <NutritionMealTable
          counter={formik.values.counters[Meal.Meal]}
          onDeleteProduct={handleProductDelete}
          objective={formik.values.objective}
          onSetProduct={handleSetProduct}
          onOptionRight={handleOptionRight}
          onOptionLeft={handleOptionLeft}
          onOptionDelete={handleOptionDelete}
          onOptionSwap={handleOptionSwap}
          editorMode={editorMode}
          equivalences={equivalencesCache}
          nutritionalInformations={nutritionalInformationsCache}
          meal={Meal.Meal}
          suplementations={formik.values.suplementations[Meal.Meal]}
          options={formik.values.nutrition[Meal.Meal] ?? {}}
          macros={formik.values.macros[Meal.Meal]}
          onCurrentOptionChange={handleCurrentOptionChange}
          onChange={formik.handleChange}
          setFieldValue={formik.setFieldValue}
          products={productsQuery?.data ?? []}
          ref={nutritionMealTableRefs[Meal.Meal]}
        />
        <NutritionMealTable
          counter={formik.values.counters[Meal.Snack]}
          onDeleteProduct={handleProductDelete}
          objective={formik.values.objective}
          onSetProduct={handleSetProduct}
          onOptionRight={handleOptionRight}
          onOptionLeft={handleOptionLeft}
          onOptionDelete={handleOptionDelete}
          onOptionSwap={handleOptionSwap}
          editorMode={editorMode}
          equivalences={equivalencesCache}
          nutritionalInformations={nutritionalInformationsCache}
          meal={Meal.Snack}
          suplementations={formik.values.suplementations[Meal.Snack]}
          options={formik.values.nutrition[Meal.Snack] ?? {}}
          macros={formik.values.macros[Meal.Snack]}
          onCurrentOptionChange={handleCurrentOptionChange}
          onChange={formik.handleChange}
          setFieldValue={formik.setFieldValue}
          products={productsQuery?.data ?? []}
          ref={nutritionMealTableRefs[Meal.Snack]}
        />
        <NutritionMealTable
          counter={formik.values.counters[Meal.Dinner]}
          onDeleteProduct={handleProductDelete}
          objective={formik.values.objective}
          onSetProduct={handleSetProduct}
          onOptionRight={handleOptionRight}
          onOptionLeft={handleOptionLeft}
          onOptionDelete={handleOptionDelete}
          onOptionSwap={handleOptionSwap}
          editorMode={editorMode}
          equivalences={equivalencesCache}
          nutritionalInformations={nutritionalInformationsCache}
          meal={Meal.Dinner}
          suplementations={formik.values.suplementations[Meal.Dinner]}
          options={formik.values.nutrition[Meal.Dinner] ?? {}}
          macros={formik.values.macros[Meal.Dinner]}
          onCurrentOptionChange={handleCurrentOptionChange}
          onChange={formik.handleChange}
          setFieldValue={formik.setFieldValue}
          products={productsQuery?.data ?? []}
          ref={nutritionMealTableRefs[Meal.Dinner]}
        />
        <NutritionMealTable
          counter={formik.values.counters[Meal.Redinner]}
          onDeleteProduct={handleProductDelete}
          objective={formik.values.objective}
          onSetProduct={handleSetProduct}
          onOptionRight={handleOptionRight}
          onOptionLeft={handleOptionLeft}
          onOptionDelete={handleOptionDelete}
          onOptionSwap={handleOptionSwap}
          editorMode={editorMode}
          equivalences={equivalencesCache}
          nutritionalInformations={nutritionalInformationsCache}
          meal={Meal.Redinner}
          suplementations={formik.values.suplementations[Meal.Redinner]}
          options={formik.values.nutrition[Meal.Redinner] ?? {}}
          macros={formik.values.macros[Meal.Redinner]}
          onCurrentOptionChange={handleCurrentOptionChange}
          onChange={formik.handleChange}
          setFieldValue={formik.setFieldValue}
          products={productsQuery?.data ?? []}
          ref={nutritionMealTableRefs[Meal.Redinner]}
        />
      </div>
      {/* Capa Derecha */}
      <div className="col-span-1 hidden md:block">
        <NutritionSideBar
          onPageChange={(page) => searchFormik.setFieldValue("page", page)}
          currentPage={searchFormik.values.page}
          lastPage={dishesQuery.data?.lastPage}
          perPage={dishesQuery.data?.perPage}
          total={dishesQuery.data?.total}
          onChange={searchFormik.handleChange}
          values={searchFormik.values}
          isLoading={
            dishesQuery.isLoading ||
            dishesQuery.isFetching ||
            suplementationsQuery.isLoading ||
            suplementationsQuery.isFetching
          }
          onSetDish={handleSetDish}
          dishes={dishesQuery?.data?.data ?? []}
          suplementations={suplementationsQuery.data ?? []}
          setFieldValue={searchFormik.setFieldValue}
          onSetSuplementation={handleSetSupplementation}
          tabsFood={TabsFood}
          selectTabFood={selectTabFood}
          handleTabChangeFood={handleTabChangeFood}
          onSuggestNewDish={handleOpenModalSuggestNewPlate}
        />
      </div>
      <div className="col-span-1 ml-5 pb-10 mt-5">
        <ButtonPrimary
          size="extraLarge"
          text={t("general.save")}
          onClick={() => formik.submitForm()}
        />
        {/* onClick={handleOpenModalAcceptSearchTraining} */}
      </div>
      {/* capa encima del contenido */}
      {openNewDivForNutrition && (
        <div
          className={`fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-end transition-all duration-300 ease-in-out ${
            openNewDivForNutrition ? "translate-y-0" : "translate-y-full"
          }`}
        >
          <div className="bg-white w-full rounded-t-lg px-6 pt-4 overflow-y-auto h-5/6">
            {/* Botón para cerrar */}
            <div className="flex justify-between items-center mt-1">
              <h1 className="text-lg font-semibold">
                {t("user.views.add_food")}
              </h1>
              <button
                className="relative text-gray-700 font-bold"
                onClick={handleOpenNewDivForNutrition}
              >
                <FontAwesomeIcon icon={faXmark} className="h-7 w-7" />
              </button>
            </div>
            <div className="flex w-full flex-col bg-white">
              <div className="flex-1">
                <div className="grid grid-cols-1">
                  <div className="col-span-full px-4 mt-2 ">
                    <TabsUser
                      tabs={TabsFood}
                      selectedTab={selectTabFood}
                      handleTabChange={handleTabChangeFood}
                    />
                  </div>
                  {selectTabFood === 1 ? (
                    <>
                      <div className="col-span-full mx-5 -mt-2">
                        <Select
                          selected={selectOptions}
                          setSelected={setSelectOptions}
                          options={optionsSelect}
                          tittle={t("accounting.views.type")}
                        />
                      </div>
                      <div className="col-span-full mx-5 mt-1">
                        <InputWithCornerHint
                          label={t("general.search")}
                          placeholder={t("user.views.name_food")}
                          type="text"
                        />
                      </div>
                      <div className="col-span-full">
                        <h1 className="text-base font-semibold leading-6 text-gray-900 border-b border-gray-200 text-center pb-3 mt-5">
                          {t("nutrition-wizard.views.meals")}
                        </h1>
                      </div>
                      <div className="overflow-y-auto h-[49vh]">
                        <div className="grid grid-cols-1 sm:grid-cols-2 flex justify-center items-center overflow-y-auto px-4">
                          {optionsComidas.map((option) => (
                            <div
                              className="border-b border-gray-200 mx-4"
                              key={`asdasd-${option}`}
                            ></div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-span-full mx-5 mt-5 border-b border-gray-200 pb-6">
                        <InputWithCornerHint
                          label={t("general.search")}
                          placeholder={t(
                            "nutrition-wizard.views.suplementation"
                          )}
                          type="text"
                        />
                      </div>
                      <div className="col-span-full mx-10 mt-5">
                        <ListSuplementation
                          options={optionsSuplementation}
                          onBreakFast={() => {}}
                          onMediaMana={() => {}}
                          onLunch={() => {}}
                          onSnack={() => {}}
                          onDinner={() => {}}
                          onPoscena={() => {}}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <AcceptOrCancelModal
        visible={toActiveModalVisible}
        title="Deseas activar el plan o dejarlo planeado"
        acceptText={t("contexts.shared.accept")}
        cancelText={t("contexts.shared.cancel")}
        onAccept={handleActiveAccept}
        onCancel={handleActiveCancel}
        isLoading={updateNutritionResult.isLoading}
      >
        <div className="grid grids-cols-1 gap-4">
          <div className="col-span-full mt-5">
            <p className="text-sm text-gray-500 px-4">
              {t("share.views.msg_searchable_plan_description")}{" "}
            </p>
          </div>
          <div className="col-span-full px-4">
            <Select
              selected={activeOptions.find(
                ({ key }) => key === formik.values.toActive
              )}
              setSelected={(option) =>
                formik.setFieldValue("toActive", option?.key)
              }
              options={activeOptions}
              tittle={""}
            />
          </div>
        </div>
      </AcceptOrCancelModal>
      <AcceptOrCancelModal
        visible={fromExpiredToActiveModalVisible}
        title="Estas editando un plan finalizado, expandirlo introduce el tiempo"
        acceptText={t("contexts.shared.accept")}
        cancelText={t("contexts.shared.cancel")}
        onAccept={handleExpiredAccept}
        onCancel={handleExpiredCancel}
        isLoading={updateNutritionResult.isLoading}
      >
        <div className="grid grids-cols-1 gap-4">
          <div className="col-span-full mt-5">
            <p className="text-sm text-gray-500 px-4">
              {t("share.views.msg_searchable_plan_description")}{" "}
            </p>
          </div>
          <div className="col-span-full px-4">
            <InputWithCornerHint
              label="Semanas"
              type="number"
              onChange={formik.handleChange}
              name="duration"
              max="100"
              min={nutritionEditableQuery.data?.plan?.duration}
              value={nutritionEditableQuery.data?.plan?.duration}
            />
            <InputWithCornerHint
              label="Fecha de finalización"
              type="date"
              value={calculatedEndDate}
              disabled
            />
          </div>
        </div>
      </AcceptOrCancelModal>
      <AcceptOrCancelModal
        visible={searchableModalVisible}
        title="Permitir buscar este plan"
        acceptText={t("contexts.shared.accept")}
        cancelText={t("contexts.shared.cancel")}
        onAccept={handleSearchableAccept}
        onCancel={handleSearchableCancel}
        isLoading={updateNutritionResult.isLoading}
      >
        <div className="grid grids-cols-1 gap-4">
          <div className="col-span-full mt-5">
            <p className="text-sm text-gray-500 px-4">
              {t("share.views.msg_searchable_plan_description")}{" "}
            </p>
          </div>
          <div className="col-span-full px-4">
            <Select
              selected={searchableOptions.find(
                ({ key }) => key === formik.values.searchable
              )}
              setSelected={(option) =>
                formik.setFieldValue("searchable", option?.key)
              }
              options={searchableOptions}
              tittle={""}
            />
          </div>
        </div>
      </AcceptOrCancelModal>
      <AcceptOrCancelModal
        visible={fromPlannedToActiveModalVisible}
        title="Estás editando un plan planeado, deseas convertirlo en el plan actual activo?"
        acceptText={t("contexts.shared.accept")}
        cancelText={t("contexts.shared.cancel")}
        onAccept={handleFromPlannedToActiveAccept}
        onCancel={handleFromPlannedToActiveCancel}
        isLoading={updateNutritionResult.isLoading}
      >
        <div className="grid grids-cols-1 gap-4">
          <div className="col-span-full mt-5">
            <p className="text-sm text-gray-500 px-4">
              {t("share.views.msg_searchable_plan_description")}{" "}
            </p>
          </div>
          <div className="col-span-full px-4">
            <Select
              selected={skipOptions.find(
                ({ key }) => key === formik.values.fromPlannedToActive
              )}
              setSelected={(option) =>
                formik.setFieldValue("fromPlannedToActive", option?.key)
              }
              options={skipOptions}
              tittle={""}
            />
          </div>
        </div>
      </AcceptOrCancelModal>

      <ModalAddNotes
        open={openModalNotes}
        Close={handleOpenModalNotes}
        tittleModal={t("nutrition-wizard.views.add_none")}
        acceptButtonText={t("general.save")}
        cancelButtonText={t("general.cancel")}
        onAdd={() => {}}
      >
        <form action="#" className="relative mt-4">
          <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
            <label htmlFor="description" className="sr-only">
              {t("user.views.write_note")}
            </label>
            <textarea
              id="description"
              name="description"
              rows={8}
              placeholder={t("user.views.write_note")}
              className=" mt-1 block w-full resize-none border-0 py-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              defaultValue={""}
            />

            {/* Spacer element to match the height of the toolbar */}
            <div aria-hidden="true">
              <div className="py-2">
                <div className="h-2" />
              </div>
              <div className="h-px" />
              <div className="py-2">
                <div className="py-px">
                  <div className="h-2" />
                </div>
              </div>
            </div>
          </div>

          <div className="absolute inset-x-px bottom-0">
            <div className="flex items-center justify-between space-x-3 border-t border-gray-200 px-2 py-2 sm:px-3">
              <div className="flex">
                <button
                  type="button"
                  className="group -my-2 -ml-2 inline-flex items-center rounded-full px-3 py-2 text-left text-gray-400"
                >
                  <PaperClipIcon
                    aria-hidden="true"
                    className="-ml-1 mr-2 h-5 w-5 group-hover:text-gray-500"
                  />
                  <span className="text-sm italic text-gray-500 group-hover:text-gray-600">
                    {t("lead.views.attach_file")}
                  </span>
                </button>
              </div>
              <div className="flex-shrink-0"></div>
            </div>
          </div>
        </form>
      </ModalAddNotes>

      <ModalAcceptSearchTraining
        open={openModalSuggestNewPlate}
        onClose={handleOpenModalSuggestNewPlate}
        title={t("user.views.request_meal")}
        acceptButtonText={t("training-wizard.views.continue")}
        cancelButtonText={t("general.cancel")}
      >
        <div className="grid grids-cols-1 gap-4">
          <div className="col-span-full mt-5">
            <p className="text-sm text-gray-500 px-4">
              {t("user.views.request_meal_description")}{" "}
            </p>
          </div>
          <div className="col-span-full px-4">
            <TextArea label="" />
          </div>
        </div>
      </ModalAcceptSearchTraining>
    </div>
  );
};
